import {
    coverageSpecificity,
    documentTitle,
    formatCoverageRange,
    isAppVisibleDocument,
    normalizeCoverageDashes,
} from '@rock/manuals';
import type { Brand, BrandId, EquipmentTypeId, ModelDocument, ModelDocumentId } from '@rock/types';
import { normalizeIdentifier } from '@rock/util';

// The documents list's filters and search, as pure data: what a row must satisfy to stay on screen.

/** The library's cross-cutting questions, as filters. `coverage` is the one with no other home. */
export const DOCUMENT_FILTERS = ['all', 'coverage', 'app-visible', 'library-only', 'ingest-failed'] as const;
export type DocumentFilter = (typeof DOCUMENT_FILTERS)[number];

/** Everything a list row is judged against. */
export interface DocumentCriteria {
    filter: DocumentFilter;
    /** Absent = every brand. */
    brandId?: string;
    /** Absent = every type. */
    typeId?: string;
    query: string;
    /** Documents linked to a model under the query's number, when the query reads as one. */
    linkedByNumber: ReadonlySet<ModelDocumentId>;
    brandsById: Map<BrandId, Brand>;
    typeTitles: Map<EquipmentTypeId, string>;
}

/** The query as a model number (letters and digits, folded), when it reads as one: 3+ chars with a digit. */
export function modelNumberQuery(query: string): string | undefined {
    const number = normalizeIdentifier(query);
    return number.length >= 3 && /\d/.test(number) ? number : undefined;
}

export function filterDocuments(documents: ModelDocument[], criteria: DocumentCriteria): ModelDocument[] {
    const needle = normalizeCoverageDashes(criteria.query.trim().toLowerCase());
    const number = modelNumberQuery(criteria.query);
    return documents.filter(
        (doc) =>
            matchesFilter(doc, criteria.filter) &&
            (!criteria.brandId || doc.brandIds.some((id) => id === criteria.brandId)) &&
            (!criteria.typeId || doc.typeIds.some((id) => id === criteria.typeId)) &&
            (matchesText(doc, needle, criteria) || (!!number && matchesNumber(doc, number, criteria.linkedByNumber))),
    );
}

/** A catalog brand as admin names it: its canonical name, which two nameplates sharing a display name never share. */
export function brandLabel(brand: Brand | undefined, id: BrandId): string {
    return brand?.canonicalName ?? id;
}

function matchesFilter(doc: ModelDocument, filter: DocumentFilter): boolean {
    switch (filter) {
        case 'coverage':
            return !!doc.coverage;
        case 'app-visible':
            return isAppVisibleDocument(doc);
        case 'library-only':
            return !isAppVisibleDocument(doc);
        case 'ingest-failed':
            return doc.ingest?.status === 'failed' || doc.ingest?.status === 'unsupported';
        default:
            return true;
    }
}

/**
 * Text search over title, id, tags, both names of each brand, type ids and titles, and the rule's
 * predicates. Both sides are dash-normalized: the display form of a range carries an en dash, but everyone
 * types the ASCII hyphen the coverage form teaches — "KSM45-KSM200" must find "KSM45–KSM200".
 */
function matchesText(
    doc: ModelDocument,
    needle: string,
    { brandsById, typeTitles }: Pick<DocumentCriteria, 'brandsById' | 'typeTitles'>,
): boolean {
    if (!needle) return true;
    const haystack = normalizeCoverageDashes(
        [
            documentTitle(doc),
            doc.id,
            ...doc.tags,
            ...doc.brandIds.flatMap((id) => {
                const brand = brandsById.get(id);
                return [brandLabel(brand, id), brand?.displayName ?? ''];
            }),
            ...doc.typeIds.flatMap((id) => [id, typeTitles.get(id) ?? '']),
            ...(doc.coverage?.prefixes ?? []),
            ...(doc.coverage?.ranges ?? []).map(formatCoverageRange),
            doc.sourceUrl ?? '',
        ]
            .join(' ')
            .toLowerCase(),
    );
    return haystack.includes(needle);
}

/** A model number finds the documents linked to a model under it, and those whose rule's prefixes or ranges match it. */
function matchesNumber(doc: ModelDocument, number: string, linked: ReadonlySet<ModelDocumentId>): boolean {
    return linked.has(doc.id) || (!!doc.coverage && coverageSpecificity(doc.coverage, number) > 0);
}
